// @flow
import * as F from 'lodash/fp';
import uniqBy from 'lodash/uniqBy';

import { Crusts, Pizza, Sauces, Toppings } from '../../pizza/fields/domain/pizza';
import { Random } from '../../../utils/Random';
import { ingredientName } from './constants';

const tasteFitTable: Object = {
  bell_peeper: {
    bell_peeper: -2,
    salami: 2,
    bacon: 1,
    teriyaki_chicken: 1,
  },
  tomato: {
    tomato: -2,
    salami: 1,
    bacon: 2,
    teriyaki_chicken: 1,
  },
  corn: {
    corn: -2,
    salami: 1,
    bacon: 1,
    teriyaki_chicken: 2,
  },
  salami: {
    salami: -2,
    crab: 2,
    squid: 1,
    shrimp: 1,
  },
  bacon: {
    bacon: -2,
    crab: 1,
    squid: 2,
    shrimp: 1,
  },
  teriyaki_chicken: {
    teriyaki_chicken: -2,
    crab: 1,
    squid: 1,
    shrimp: 2,
  },
  crab: {
    bell_peeper: 2,
    tomato: 1,
    corn: 1,
    crab: -2,
  },
  squid: {
    bell_peeper: 1,
    tomato: 2,
    corn: 1,
    squid: -2,
  },
  shrimp: {
    bell_peeper: 1,
    tomato: 1,
    corn: 2,
    shrimp: -2,
  },
};

const sauceFitTable: Object = {
  tomato_sauce: { vegetable: 1 },
  white_sauce: { meat: 1 },
  basil_sauce: { seafood: 1 },
};

const crustFitTable: Object = {
  thin_crust: { white_sauce: 1 },
  normal_crust: { basil_sauce: 1 },
  thick_crust: { tomato_sauce: 1 },
};

const multiplier = [0.6, 0.6, 0.6, 0.6, 0.6, 0.6, 0.7, 0.8, 0.9, 1, 1.1, 1.2, 1.3, 1.4, 1.5, 1.5, 1.6, 1.7, 1.7, 1.7];
const starDiff = [-1, -1, -1, -1, -1, -1, -0.5, -0.5, -0.5, 0, 0, 0, 0.5, 0.5, 1, 1, 1, 1.5, 1.5, 1.5];

const toppingTypeNames = {
  vegetable: '野菜',
  meat: 'お肉',
  seafood: 'シーフード',
};

export const pizzaGameRules = function(
  todayPizza: Pizza,
  yesterdayPizza: Pizza
): {
  customerMultiplier: number,
  starDiff: number,
  score: number,
} {
  let score = 0;
  const uniqTodayToppings = uniqBy(todayPizza.toppings, 'topping');
  const uniqYesterdayToppings = uniqBy(yesterdayPizza.toppings, 'topping');

  for (let todayTopping of uniqTodayToppings) {
    for (let yesterdayTopping of uniqYesterdayToppings) {
      let tasteFitScore = tasteFitTable[yesterdayTopping.topping][todayTopping.topping] || 0;

      score += tasteFitScore;
    }
  }

  // 5
  let todayUniqToppingTypes = uniqBy(uniqTodayToppings, t => t.type);
  if (todayUniqToppingTypes.length === 1 || todayUniqToppingTypes.length === 3) {
    score += -3;
  }

  // 6
  const categories = ['vegetable', 'meat', 'seafood'];
  categories.forEach(cat => {
    const sameCategory = todayPizza.toppings.filter(t => t.type === cat);
    if (sameCategory.length >= 2) {
      score += sauceFitTable[todayPizza.sauce][cat] || 0;
    }
  });

  // 7
  score += crustFitTable[todayPizza.crust][todayPizza.sauce] || 0;

  // normalize
  if (score > 10) score = 10;
  if (score < -9) score = -9;

  return {
    score,
    customerMultiplier: multiplier[score + 9],
    starDiff: starDiff[score + 9],
  };
};

export const genComments = function(
  todayPizza: Pizza,
  yesterdayPizza: Pizza,
  blacklist: string[] = []
): Array<[0 | 1, string]> {
  let comments: Array<[0 | 1, string]> = [];

  let todayToppingCount = F.countBy(t => t.topping, todayPizza.toppings);
  let yesterdayToppingCount = F.countBy(t => t.topping, yesterdayPizza.toppings);
  let todayToppingTypeCount = F.countBy(t => t.type, todayPizza.toppings);
  let yesterdayToppingTypeCount = F.countBy(t => t.type, yesterdayPizza.toppings);

  let sameToppingCount = F.pipe(
    F.toPairs,
    F.sumBy(([topping, count]) => Math.min(count, yesterdayToppingCount[topping] || 0))
  )(todayToppingCount);
  if (sameToppingCount >= 2) {
    comments.push([0, '昨日と同じ食材はあきちゃうな。']);
  }

  let toppingTypes = ['vegetable', 'meat', 'seafood'];
  for (let toppingType of toppingTypes) {
    if (todayToppingTypeCount[toppingType] >= 2 && yesterdayToppingTypeCount[toppingType] >= 2) {
      comments.push([0, `昨日は${toppingTypeNames[toppingType]}のピザだったから、今日はちがう種類がよかったな。`]);
    }
    if (todayToppingCount[toppingType] === 3) {
      comments.push([0, `トッピングが${toppingTypeNames[toppingType]}だけだと、あまりおいしいと思わなかったな。`]);
    }
  }

  if (todayToppingTypeCount['meat'] >= 2 && todayPizza.sauce == 'white_sauce') {
    comments.push([1, 'お肉のピザには、ホワイトソースが合うよね。']);
  }
  if (todayToppingTypeCount['vegetable'] >= 2 && todayPizza.sauce == 'tomato_sauce') {
    comments.push([1, '野菜のピザには、トマトソースが合うよね。']);
  }
  if (todayToppingTypeCount['seafood'] >= 2 && todayPizza.sauce == 'basil_sauce') {
    comments.push([1, 'シーフードのピザには、バジルソースが合うよね。']);
  }

  if (yesterdayToppingTypeCount['vegetable'] >= 2 && todayToppingTypeCount['meat'] >= 2) {
    comments.push([1, '野菜のピザの次の日が、お肉のピザなのはうれしいね。']);
  }
  if (yesterdayToppingTypeCount['meat'] >= 2 && todayToppingTypeCount['seafood'] >= 2) {
    comments.push([1, 'お肉のピザの次の日が、シーフードのピザなのはうれしいね。']);
  }
  if (yesterdayToppingTypeCount['seafood'] >= 2 && todayToppingTypeCount['vegetable'] >= 2) {
    comments.push([1, 'シーフードのピザの次の日が、野菜のピザなのはうれしいね。']);
  }

  if ((crustFitTable[todayPizza.crust][todayPizza.sauce] || 0) > 0) {
    comments.push([1, `${ingredientName(todayPizza.crust)}と${ingredientName(todayPizza.sauce)}って合うわね。`]);
  }

  let { score } = pizzaGameRules(todayPizza, yesterdayPizza);
  comments = comments.filter(([n]) => n === (score > 0 ? 1 : 0));

  let uniqComments = comments.filter(([, content]) => !F.includes(content)(blacklist));
  if (uniqComments.length) return uniqComments;
  else return comments;
};

export const pizzaG: Random<Pizza> = Random.map3(
  Random.arrayOfLength(Random.of(...(Object.values(Toppings): any)), 3),
  Random.of(...Crusts),
  Random.of(...Sauces),
  (toppings, crust, sauce) => new Pizza(crust, sauce, toppings)
).filter(pizza => F.uniq(pizza.toppings).length === 3);
